import { Component, EventEmitter, HostListener, Inject, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";

import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

import { Observable } from "rxjs";
import { select, Store } from "@ngrx/store";
import { AppState, sitesStateIsLoading } from "src/app/store/reducers";
import { createPos, updatePos } from "src/app/store/actions/sites/sites.actions";

import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { PosType, SitePosDTO, VirtualPosDTO } from "src/app/models/Sites/sitePosDTO";
import { SiteFuelPointDTO } from "src/app/models/Sites/siteFuelPointDTO";
import { SitePeripheralDTO } from "src/app/models/Sites/sitePeripheralDTO";
import { PropertyDTO } from "src/app/models/Properties/propertyDTO";
import { SiteMultimediaDTO } from "src/app/models/Sites/siteMultimediaDTO";
import { Dialog, GuardConfirmationDialogData } from "src/app/models/dialog/dialog.model";
import { CommunicationMethodTypeCode, PROTOCOL_FORM_PARAMS } from "src/app/models/protocol/protocol.model";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { setCommunicationMethodData, setProtocolFormValues } from "src/app/shared/utils/protocolFormUtils";
import { isAuthenticated } from "src/app/shared/utils/authenticationUtils";

import { ConfirmationDialogComponent } from "src/app/shared/modules/dialogs/confirmation-dialog/confirmation-dialog.component";
import { CanComponentDeactivate } from "src/app/shared";
import { CategoriesAndPropsUtils } from "src/app/shared/utils/categoriesAndPropsUtils";

const POS_FORM_PARAMS = {
  code: "code",
  enabled: "enabled",
  posNumber: "posNumber",
  serialNumber: "serialNumber",
  description: "description",
  connectedFuelPoints: "connectedFuelPoints",
  connectedPeripherals: "connectedPeripherals",
  connectedMultimedia: "connectedMultimedia",
  protocolTypeCode: "protocolTypeCode",
  protocol: "protocol",
  virtualPos: "virtualPos",
  hasPrinter: "hasPrinter",
  connectedPumps: "connectedPumps",
  posType: "posType",
  posProperties: "posProperties",
  properties: "properties", //vpos properties
};

@Component({
  selector: "app-pos-form-dialog",
  templateUrl: "./pos-form-dialog.component.html",
  styleUrls: ["./pos-form-dialog.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class PosFormDialogComponent implements OnInit, CanComponentDeactivate {
  @Output() getPosForm: EventEmitter<FormGroup> = new EventEmitter<FormGroup>();

  isLoading$: Observable<boolean>;

  siteCode: string;
  currentPos: SitePosDTO;

  fuelPointList: SiteFuelPointDTO[];
  peripheralList: SitePeripheralDTO[];
  multimediaList: SiteMultimediaDTO[];
  posTypeList: PosType[];
  propertyList: PropertyDTO[];

  isCreate: boolean = false;

  posDataForm: FormGroup;

  tabs = ["POS"];
  selectedTab = new FormControl(0);

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<PosFormDialogComponent>,
    private fb: FormBuilder,
    private router: Router,
    private store: Store<AppState>,
    private snackbarService: SnackbarService,
    private translate: TranslateService
  ) {
    this.isLoading$ = this.store.pipe(select(sitesStateIsLoading));
  }

  ngOnInit(): void {
    this.setDialogData();
    this.createPosForm();
    this.setFormValues(this.currentPos);
    this.setVPosFormArray(this.currentPos);
  }

  setDialogData() {
    this.isCreate = this.data.isCreate;
    this.siteCode = this.data.siteCode;
    this.currentPos = this.data.currentPos;
    this.fuelPointList = this.data.fuelPointList;
    this.peripheralList = this.data.peripheralList;
    this.multimediaList = this.data.multimediaList;
    this.posTypeList = this.data.posTypeList;
    this.propertyList = this.data.propertyList;
  }

  createPosForm() {
    this.posDataForm = this.fb.group({
      [POS_FORM_PARAMS.enabled]: [true],
      [POS_FORM_PARAMS.posNumber]: [
        null,
        [Validators.required, Validators.min(1), Validators.max(255)],
      ],
      [POS_FORM_PARAMS.serialNumber]: [null, Validators.maxLength(50)],
      [POS_FORM_PARAMS.description]: [null, [Validators.required, Validators.maxLength(45)]],
      [POS_FORM_PARAMS.connectedFuelPoints]: [null],
      [POS_FORM_PARAMS.connectedPeripherals]: [null],
      [POS_FORM_PARAMS.connectedMultimedia]: [null],
      [POS_FORM_PARAMS.protocolTypeCode]: [null, [Validators.required]],
      [POS_FORM_PARAMS.protocol]: this.fb.group({}),
      [POS_FORM_PARAMS.virtualPos]: this.fb.array([]),
      [POS_FORM_PARAMS.posProperties]: this.fb.array([]),
    });
    this.subscribeFormChanges(this.posDataForm);
  }

  subscribeFormChanges(form: FormGroup) {
    form.valueChanges.subscribe(() => {
      this.getPosPristine();
    });
  }

  setFormValues(pos: SitePosDTO, duplication?: boolean) {
    this.posDataForm.controls.enabled.setValue(pos?.enabled ?? false);
    this.posDataForm.controls.posNumber.setValue(
      duplication ? null : pos?.posNumber ?? null
    );
    this.posDataForm.controls.serialNumber.setValue(
      duplication ? null : pos?.serialNumber ?? null
    );
    this.posDataForm.controls.description.setValue(pos?.description ?? null);
    this.posDataForm.controls.protocolTypeCode.setValue(
      pos?.protocolTypeCode ?? null
    );
    this.posDataForm.controls.connectedFuelPoints.setValue(
      pos?.connectedFuelPoints ?? null
    );
    this.posDataForm.controls.connectedPeripherals.setValue(
      pos?.connectedPeripherals ?? null
    );
    this.posDataForm.controls.connectedMultimedia.setValue(
      pos?.connectedMultimedia ?? null
    );

    setProtocolFormValues(this.posDataForm, pos);
  }

  setVPosFormArray(pos: SitePosDTO) {
    const vPosList = this.getFormArray(POS_FORM_PARAMS.virtualPos);
    this.clearFormArray(vPosList);

    pos.virtualPos.forEach((vPos: VirtualPosDTO) => {
      vPosList.push(this.createVPosFormGroup(vPos));
      this.tabs.push("VPos");
    });
  }

  duplicate() {
    this.setFormValues(this.currentPos, true);

    this.isCreate = true;
    this.currentPos = null;
  }

  onSubmit() {
    if (!this.posDataForm.valid) {
      this.posDataForm.markAllAsTouched();
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.formInvalid"),
        type: SnackbarType.ERROR,
      });
      return;
    }
    
    if (this.posDataForm.pristine && !this.isCreate) {
      this.snackbarService.show({
        message: this.translate.instant("SiteManagement.NoChanges"),
        type: SnackbarType.WARNING,
      });
      return;
    }

    const protocolFormGroup = this.posDataForm.get(
      POS_FORM_PARAMS.protocol
    ) as FormGroup;
    let pos: SitePosDTO = {
      code: this.currentPos?.code ?? null,
      siteCode: this.siteCode ?? null,
      enabled: this.posDataForm.controls.enabled.value ?? false,
      posNumber: this.posDataForm.controls.posNumber.value ?? null,
      serialNumber: this.posDataForm.controls.serialNumber.value ?? null,
      description: this.posDataForm.controls.description.value ?? null,
      connectedFuelPoints: this.posDataForm.controls.connectedFuelPoints.value ?? null,
      connectedPeripherals: this.posDataForm.controls.connectedPeripherals.value ?? null,
      connectedMultimedia: this.posDataForm.controls.connectedMultimedia.value ?? null,
      protocolTypeCode: this.posDataForm.controls.protocolTypeCode.value ?? null,
      communicationMethodTypeCode: protocolFormGroup.get(
        PROTOCOL_FORM_PARAMS.communicationMethodTypeCode
      ).value
        ? CommunicationMethodTypeCode.SERIAL_CONNECTION
        : CommunicationMethodTypeCode.IP_PROTOCOL,
      communicationMethodData: setCommunicationMethodData(protocolFormGroup),
      properties: CategoriesAndPropsUtils.setFormProperties(
        this.posDataForm.controls.posProperties.value
      ),
      virtualPos: this.setVPosArray(),
    };

    if (this.isCreate) {
      this.store.dispatch(createPos({ pos }));
    } else {
      this.store.dispatch(updatePos({ pos }));
    }

    this.posDataForm.markAsUntouched();
    this.posDataForm.markAsPristine();
    this.getPosPristine();
  }

  setVPosArray() {
    return this.posDataForm
      .get(POS_FORM_PARAMS.virtualPos)
      .value.map((vPos) => ({
        ...vPos,
        properties: CategoriesAndPropsUtils.setFormProperties(vPos.properties),
      }));
  }

  addTab() {
    this.addVPos();
    this.tabs.push("VPos");
    this.selectedTab.setValue(this.tabs.length - 1);
  }

  removeTab(index: number) {
    this.posDataForm.markAsDirty();
    this.removeVPos(index - 1);
    this.tabs.splice(index, 1);
  }

  resetTabs() {
    this.tabs = ["POS"];
  }

  createVPosFormGroup(virtualPos?: VirtualPosDTO): FormGroup {
    const { 
      code, 
      posNumber, 
      description, 
      hasPrinter, 
      connectedPumps, 
      connectedPeripherals, 
      connectedMultimedia, 
      posType
    } = virtualPos || {};
    return this.fb.group({
      [POS_FORM_PARAMS.code]: [code ? code : null],
      [POS_FORM_PARAMS.posNumber]: [
        posNumber || posNumber === 0 ? posNumber : null,
        [Validators.required, Validators.min(0), Validators.max(255)],
      ],
      [POS_FORM_PARAMS.description]: [
        description ? description : null,
        [Validators.required, Validators.maxLength(45)],
      ],
      [POS_FORM_PARAMS.hasPrinter]: [hasPrinter ? hasPrinter : false],
      [POS_FORM_PARAMS.connectedPumps]: [
        connectedPumps ? connectedPumps : null,
      ],
      [POS_FORM_PARAMS.connectedPeripherals]: [
        connectedPeripherals ? connectedPeripherals : null,
      ],
      [POS_FORM_PARAMS.connectedMultimedia]: [
        connectedMultimedia ? connectedMultimedia : null,
      ],
      [POS_FORM_PARAMS.posType]: [posType? posType : null, [Validators.required]],
      [POS_FORM_PARAMS.properties]: this.fb.array([]),
    });
  }

  addVPos() {
    const vPosList = this.getFormArray(POS_FORM_PARAMS.virtualPos);
    vPosList.push(this.createVPosFormGroup());
  }

  removeVPos(index: number) {
    const vPosList = this.getFormArray(POS_FORM_PARAMS.virtualPos);
    vPosList.removeAt(index);
  }

  getFormArray(formParam: string): FormArray {
    return this.posDataForm.get(formParam) as FormArray;
  }

  clearFormArray(formArray: FormArray) {
    formArray.clear();
    this.resetTabs();
  }

  clearFormControl(form: FormGroup, control: string): void {
    form.controls[control].setValue(null);
    form.markAsDirty();
  }

  getPosPristine() {
    this.getPosForm.emit(this.posDataForm);
  }

  close() {
    this.dialogRef.close();
  }

  @HostListener("window:beforeunload")
  canDeactivate(): Observable<boolean> | boolean {
    return this.isNavigationAllowed();
  }

  isNavigationAllowed(): Observable<boolean> | boolean {
    if (!isAuthenticated(this.router.url) || this.posDataForm.pristine) {
      return true;
    } else {
      return false;
    }
  }

  confirmDialog(dialog: any): Observable<boolean> {
    const dialogData: Dialog = GuardConfirmationDialogData;
    const dialogRef = dialog.open(ConfirmationDialogComponent, dialogData);
    return dialogRef.afterClosed();
  }
}
